import * as ImageDataEdit from './ImageDataEdit.js';
import { setTrackpad } from './DetectTrackpad.js';
import DragCamera from './DragCamera.js';
import ImageDataManager from './ImageDataManager.js';

export default class ToolOptions {
  constructor(
    dragCamera, objectManager, colorManager,dragToolManager, drawingToolKit, selection,
    colorPalette, gradientPalette, colorPicker, exampleWindowContainer, gridLarge, gridSmall, cursorHighlighter,
    saveManager, keyboard, toolOptionToggler, toolOptionBackup, scaleInputExport, toolSection, mulLangTexts, requestDraw
  ) {
    this._dragCamera = dragCamera;
    this._objectManager = objectManager;
    this._imageDataManager = objectManager.getImageDataManagerProxy();
    this._dragToolManager = dragToolManager;
    this._drawingToolKit = drawingToolKit;
    this._selection = selection;
    this._colorPalette = colorPalette;
    this._gradientPalette = gradientPalette;
    this._colorPicker = colorPicker;
    this._exampleWindowContainer = exampleWindowContainer;
    this._gridLarge = gridLarge;
    this._gridSmall = gridSmall;
    this._cursorHighlighter = cursorHighlighter;
    this._saveManager = saveManager;
    this._keyboard = keyboard;
    this._toolOptionToggler = toolOptionToggler;
    this._toolOptionBackup = toolOptionBackup;
    this._scaleInputExport = scaleInputExport;
    this._toolSection = toolSection;
    this._mulLangTexts = mulLangTexts;
    this._requestDraw = requestDraw;
    this._elGroup = document.querySelector('#toolOptionGroup');
    this._elBucketTargetPara = this._elGroup.querySelector('.bucketTargetPara');
    this._elBucketIncludeDiagonalsPara = this._elGroup.querySelector('.bucketIncludeDiagonalsPara')
    this._elSaveFileName = this._elGroup.querySelector('[name="saveFileName"]');
    this._colorDist = new ColorDist(drawingToolKit, colorManager, this._elGroup);

    this._elGroup.addEventListener('input', e => this._handleInput(e));
    this._elGroup.addEventListener('click', e => this._handleClick(e));
  }
  _handleInput(e) {
    const name = e.target.getAttribute('name');
    switch(name) {
    case 'configGridLargeActive':
      this._gridLarge.active = e.target.checked;
      this._requestDraw();
      break;
    case 'configGridLargeX':
      this._gridLarge.width = parseInt(e.target.value);
      this._requestDraw();
      break;
    case 'configGridLargeY':
      this._gridLarge.height = parseInt(e.target.value);
      this._requestDraw();
      break;
    case 'configGridSmallActive':
      this._gridSmall.active = e.target.checked;
      this._requestDraw();
      break;
    case 'configGridSmallX':
      this._gridSmall.width = parseInt(e.target.value);
      this._requestDraw();
      break;
    case 'configGridSmallY':
      this._gridSmall.height = parseInt(e.target.value);
      this._requestDraw();
      break;
    case 'configCursorPixel':
      this._cursorHighlighter.showCursorPixel = e.target.checked;
      break;
    case 'configFrontLayerOpacity':
      ImageDataManager.frontLayerOpacity = parseFloat(e.target.value);
      this._requestDraw();
      break;
    case 'configBackLayerOpacity':
      ImageDataManager.backLayerOpacity = parseFloat(e.target.value);
      this._requestDraw();
      break;
    case 'configFrontFrameOpacity':
      ImageDataManager.frontFrameOpacity = parseFloat(e.target.value);
      this._requestDraw();
      break;
    case 'configBackFrameOpacity':
      ImageDataManager.backFrameOpacity = parseFloat(e.target.value);
      this._requestDraw();
      break;
    case 'moveInertiaDeceleration':
      DragCamera.moveInertiaDeceleration = [0,.8,.85,.9][e.target.value];
      break;
    case 'zoomInertiaDeceleration':
      DragCamera.zoomVelUnit = [.2,.06,.038,.03][e.target.value];
      DragCamera.zoomInertiaDeceleration = [0,.75,.85,.9][e.target.value];
      break;
    case 'reverseKeyMove':
      this._keyboard.reverseKeyMove = e.target.checked ? -1 : 1;
      break;
    case 'reverseTrackpadMove':
      DragCamera.reverseTrackpadMove = e.target.checked ? -1 : 1;
      break;
    case 'mouseButton0':
    case 'mouseButton1':
    case 'mouseButton2':
    case 'mouseButton3':
    case 'mouseButton4':
      this._dragToolManager.buttons[parseInt(name.at(-1))] = e.target.value;
      break;
    case 'inputDevice':
      switch(e.target.value) {
        case 'auto':
          setTrackpad();
          break;
        case 'mouse':
          setTrackpad(false);
          break;
        case 'trackpad':
          setTrackpad(true);
          break;
      }
      break;
    case 'exportScale':
      this._scaleInputExport.update();
      break;
    case 'openPXARCH':
    case 'openPXARCHAdding':
      if(e.target.files.length) {
        document.body.classList.add('dontTouchCuzloadingPXARCH');
        const file = e.target.files[0];
        this._saveManager.loadPXARCH(file, name === 'openPXARCHAdding').catch(e => {
          alert(e.message);
          throw e;
        }).finally(() => {
          const fname = file.name.replace(/( \([0-9]+\))?\.pxarch$/, '');
          this._elSaveFileName.value = fname;
          document.body.classList.remove('dontTouchCuzloadingPXARCH');
          this._toolOptionBackup.setBackup();
        });
        e.target.value = '';
        this._toolOptionToggler.close();
      }
      break;
    case 'penThick':
      this._drawingToolKit.penThick = Number(e.target.value) || 1;
      break;
    case 'eraserThick':
      this._drawingToolKit.eraserThick = Number(e.target.value) || 1;
      break;
    case 'bucketMode':
      this._drawingToolKit.bucketMode = e.target.value;
      switch(e.target.value) {
      case 'normal':
        this._elBucketTargetPara.classList.add('hidden');
        this._elBucketIncludeDiagonalsPara.classList.remove('hidden');
        break;
      case 'addEdge':
      case 'leaveEdge':
        this._elBucketTargetPara.classList.add('hidden');
        this._elBucketIncludeDiagonalsPara.classList.add('hidden');
        break;
      case 'replaceColor':
        this._elBucketTargetPara.classList.remove('hidden');
        this._elBucketIncludeDiagonalsPara.classList.add('hidden');
        break;
      }
      break;
    case 'bucketTarget':
      this._drawingToolKit.bucketTarget = e.target.value;
      break;
    case 'bucketIncludeDiagonals':
      this._drawingToolKit.bucketIncludeDiagonals = e.target.checked;
      break;
    case 'bucketSimilarColor':
      this._colorDist.show(e.target.checked);
      break;
    case 'bucketDistRGB':
      this._drawingToolKit.bucketDistRGB = e.target.value;
      break;
    case 'bucketDistA':
      this._drawingToolKit.bucketDistA = e.target.value;
      break;
    case 'shapesThick':
      this._drawingToolKit.shapesThick = Number(e.target.value) || 1;
      break;
    case 'shapesOption':
      this._drawingToolKit.shapesType = e.target.value;
      break;
    case 'selectionTarget':
      this._selection.setTarget(e.target.value);
      break;
    case 'selectionAlgo':
      this._imageDataManager.setSelectionTransformAlgo(e.target.value);
      this._requestDraw();
      break;
    case 'pasteLargerImage':
      this._selection.pasteLargeImage = e.target.value;
      break;
    case 'colorPalette':
      this._colorPalette.show(e.target.checked);
      break;
    case 'colorGradient':
      this._gradientPalette.show(e.target.checked);
      break;
    case 'colorPicker':
      this._colorPicker.show(e.target.checked);
      break;
    }
  }
  _handleClick(e) {
    const name = e.target.getAttribute('name');
    switch(name) {
    case 'plusUISize':
    case 'minusUISize':
    {
      let size = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--base-size'));
      size = Math.max(5, Math.min(size + (name === 'plusUISize' ? 1 : -1) * .5, 10));
      const elConfig = this._elGroup.querySelector('.optionList[data-mode="config"]');
      let rect = elConfig.getBoundingClientRect();
      const relX = (e.x - rect.x) / rect.width;
      const relY = (e.y - rect.y) / rect.height;
      document.documentElement.style.setProperty('--base-size', size + 'px');
      localStorage.baseSize = size;
      rect = elConfig.getBoundingClientRect();
      elConfig.style.left = e.x - rect.width * relX + 'px';
      elConfig.style.top = e.y - rect.height * relY + 'px';
      this._colorPicker.redraw();
      break;
    }
    case 'mouseButtonDefault':
    {
      this._dragToolManager.buttons = ['draw', 'move', 'eyedropper', '', ''];
      const elMouseButtons = this._elGroup.querySelectorAll('select[name^="mouseButton"]');
      this._dragToolManager.buttons.forEach((v, i) => {
        elMouseButtons[i].value = v;
      });
      break;
    }
    case 'downloadPXARCH':
      e.target.disabled = true;
      this._saveManager.createPXARCH().then(blob => {
        const url = URL.createObjectURL(blob);
        this.download(url, 'pxarch');
        URL.revokeObjectURL(url);
      }).catch(e => {
        alert(e.message);
        throw e;
      }).finally(() => {
        e.target.disabled = false;
      });
      this._toolOptionToggler.close();
      break;
    case 'openPXARCHButton':
    case 'openPXARCHAddingButton':
      e.target.nextElementSibling.click();
      break;
    case 'exportSinglePNG':
      this._imageDataManager.exportSinglePNG(this._scaleInputExport.get(), dataURL => {
        this.download(dataURL, 'png');
      });
      this._toolOptionToggler.close();
      break;
    case 'exportConcatenatedPNG':
      this._imageDataManager.exportConcatenatedPNG(this._scaleInputExport.get(), dataURL => {
        this.download(dataURL, 'png');
      });
      this._toolOptionToggler.close();
      break;
    case 'exportZIP':
      e.target.disabled = true;
      this._objectManager.exportZIP(this._scaleInputExport.get(), blob => {
        const url = URL.createObjectURL(blob);
        this.download(url, 'zip');
        URL.revokeObjectURL(url);
        e.target.disabled = false;
      });
      this._toolOptionToggler.close();
      break;
    case 'exportCopy':
      if(this._imageDataManager.clipboardPermitted) {
        this._imageDataManager.copySelectedFrameToClipboard(this._scaleInputExport.get()).then(() => {
          e.target.querySelector('.popupLabel').classList.add('show');
        });
      } else {
        alert(this._mulLangTexts.get('ALERT_NOSUPPORT_COPY'));
      }
      break;
    case 'backupLoad':
      this._toolOptionBackup.load();
      break;
    case 'pasteOnCanvas':
      this._selection.pasteFromClipboard(null).then(() => this._toolSection.setMode('selection')).catch(err => {
        if(err.name === 'DataError') {
          alert(this._mulLangTexts.get('ALERT_CLIPBOARD_DATA_ERROR'));
        }
      });
      this._toolOptionToggler.close();
      break;
    case 'pasteAsExample':
      this._exampleWindowContainer.addWindow(this._dragToolManager.buttons, this._keyboard);
      this._toolOptionToggler.close();
      break;
    case 'pasteToPalette':
      if(this._imageDataManager.clipboardPermitted) {
        e.target.disabled = true;
        this._colorPalette.checkClipboard().then(blob => {
          this._colorPalette.paste(blob);
          this._toolOptionToggler.close();
        }).catch(err => {
          if(err.name === 'DataError') {
            alert(this._mulLangTexts.get('ALERT_CLIPBOARD_DATA_ERROR_PALETTE'));
          } else if(err !== 'noimg') throw e;
        }).finally(() => {
          e.target.disabled = false;
        });
      } else {
        alert(this._mulLangTexts.get('ALERT_NOSUPPORT_COPY'));
      }
      break;
    case 'clearPalette':
      if(window.confirm(this._mulLangTexts.get('CONFIRM_CLEAR_PALETTE'))) {
        this._colorPalette.clear();
        this._toolOptionToggler.close();
      }
      break;
    case 'shapesOption':
      this._selection.finishIfNot();
      this._toolSection.setMode('shapes');
      this._toolOptionToggler.close();
      break;
    }
  }
  download(href, extension) {
    const a = document.createElement('a');
    a.href = href;
    a.download = this._saveFileName(extension);
    a.click();
  }
  _saveFileName(extension) {
    const fname = this._elSaveFileName.value;
    if(fname) {
      return `${fname}.${extension}`;
    } else {
      return `PixelArch_${new Date().toLocaleTimeString().replace(/:/g, '-')}.${extension}`;
    }
  }
}

class ColorDist {
  constructor(drawingToolKit, colorManager, elToolOptionGroup) {
    this._drawingToolKit = drawingToolKit;
    this._elColorDist = elToolOptionGroup.querySelector('.toolOptionColorDist');
    const arrElColor = elToolOptionGroup.querySelectorAll('.toolOptionColorDist .color');
    const elBucketDistRGB = elToolOptionGroup.querySelector('[name="bucketDistRGB"]');
    const elBucketDistA = elToolOptionGroup.querySelector('[name="bucketDistA"]');
    const distantColors = [[255, 0, 0, 1], [255, 128, 0, 1]];
    arrElColor.forEach((v, i) => {
      v.addEventListener('click', () => {
        distantColors[i] = colorManager.get();
        v.style.backgroundColor = `rgba(${distantColors[i].toString()})`;
        calcColorDist();
      });
    });
    function calcColorDist() {
      drawingToolKit.bucketDistRGB = ImageDataEdit.ciede2000(...ImageDataEdit.rgbToLab(...distantColors[0]), ...ImageDataEdit.rgbToLab(...distantColors[1]));
      drawingToolKit.bucketDistA = Math.abs(distantColors[0][3] - distantColors[1][3]);
      elBucketDistRGB.value = drawingToolKit.bucketDistRGB.toFixed(1);
      elBucketDistA.value = drawingToolKit.bucketDistA.toFixed(2);
    }
    calcColorDist();
    arrElColor[0].style.backgroundColor = `rgba(${distantColors[0].toString()})`;
    arrElColor[1].style.backgroundColor = `rgba(${distantColors[1].toString()})`;
  }
  show(yes) {
    if(yes) {
      this._drawingToolKit.bucketSimilarColor = true;
      this._elColorDist.classList.remove('hidden');
    } else {
      this._drawingToolKit.bucketSimilarColor = false;
      this._elColorDist.classList.add('hidden');
    }
  }
}
